import { useEffect } from 'react';
import useCallStore from '../store/useCallStore';
import Image from 'next/image';
import { useSession } from 'next-auth/react';
import { FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import { IoCall } from 'react-icons/io5';
import { MdCallEnd, MdCallReceived } from 'react-icons/md';
import { useRouter } from 'next/router';
import { GrRefresh } from 'react-icons/gr';

const CallButton = ({ onClick, className, title, children, disabled = false }) => (
	<button
		onClick={onClick}
		disabled={disabled}
		className={`relative group ${className}`}
		title={title}
		aria-label={title}
	>
		<div className="absolute inset-0 bg-white/10 rounded-full blur-md group-hover:blur-lg transition-all duration-300" />
		<div className="relative flex items-center justify-center">
			{children}
		</div>
	</button>
);

const CallModal = () => {
	const router = useRouter();
	const {
		isOpen,
		callStatus,
		isMuted,
		callDuration,
		targetUserAvatar,
		targetUserName,
		targetUserId,
		startCall,
		endCall,
		toggleMute,
		formatCallDuration,
		closeModal,
		acceptCall,
		rejectCall,
		isOnline,
		rejoinCall,
		playRemoteAudio
	} = useCallStore();

	const { data: session } = useSession();

	useEffect(() => {
		if (callStatus === 'calling') {
			startCall();
		}
		if (callStatus === 'connected') {
			playRemoteAudio();
		}
	}, [callStatus, startCall, playRemoteAudio]);



	if (!isOpen) return null;

	const renderCallStatus = () => {
		switch (callStatus) {
			case 'incoming':
				return "مكالمة واردة";
			case 'connected':
				return "مكالمة جارية";
			case 'calling':
				return "جاري الاتصال...";
			case 'disconnected':
				return "انتهت المكالمة";
			default:
				return null;
		}
	};

	return (
		<div className="fixed inset-0 bg-gradient-to-b from-[#45AFFD] to-[#03365C] flex items-center justify-center z-50 p-4 backdrop-blur-sm">
			<div className="rounded-xl w-full max-w-md overflow-hidden bg-white/10 backdrop-blur-md shadow-2xl relative">
				{/* Header with gradient */}
				<div className="p-6 text-white">
					<div className="flex flex-col items-center gap-4">
						<div className="relative w-32 h-32 md:w-40 md:h-40 group">
							<div className="absolute inset-0 bg-white/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300" />
							<Image
								src={targetUserAvatar || "/tqneen/default-user.jpg"}
								alt={targetUserName || "المحامي"}
								width={160}
								height={160}
								className="relative object-cover rounded-sm mask-brand border-4 border-white/20 transition-transform duration-300 group-hover:scale-105"
								priority
							/>
						</div>
						<div className="text-center">
							<h1 className="text-2xl md:text-3xl font-bold mb-2">
								{targetUserName}
							</h1>
							<h2 className="text-lg md:text-xl font-semibold text-white/90">
								{renderCallStatus()}
							</h2>
							{callStatus === 'connected' && (
								<p className="text-base md:text-lg opacity-90 mt-2">
									{formatCallDuration(callDuration)}
								</p>
							)}
							{!isOnline && (
								<p className="text-sm text-red-300 mt-2">جارٍ إعادة الاتصال...</p>
							)}
						</div>
					</div>
				</div>

				{/* Call content */}
				<div className="p-6 bg-white/5 backdrop-blur-sm">
					{/* Caller info */}
					<div className="flex flex-col items-center mb-8">
						<div className="relative w-24 h-24 md:w-28 md:h-28 group">
							<div className="absolute inset-0 bg-white/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300" />
							<Image
								src={session?.user?.avatar || "/tqneen/default-user.jpg"}
								alt={session?.user?.name || "المحامي"}
								width={110}
								height={120}
								className="relative object-cover rounded-sm mask-brand border-4 border-white/20 transition-transform duration-300 group-hover:scale-105"
								priority
							/>
						</div>
					</div>

					{/* Call controls */}
					<div className="flex flex-col items-center space-y-8">
						<div className="flex gap-6 items-center">
							{/* Always show close/end button, but its function changes */}
							<CallButton
								onClick={() => {
									if (callStatus === 'connected') {
										endCall(); // Just end the call, don't close modal
									} else if (callStatus === 'incoming') {
										rejectCall();
									} else {
										closeModal(); // Close modal if disconnected, etc.
										setTimeout(() => {
											const isRated = localStorage.getItem('isRated') || false;
											if (session?.user?.role === 'customer' && isRated !== targetUserId) {
												localStorage.setItem('isRated', targetUserId);
												router.push(`/rate/${targetUserId}`);
											}
										}, 1500);
									}
								}}
								className="w-20 h-20 md:w-25 md:h-25 bg-red-600/90 text-white rounded-md hover:bg-red-700 transition-all duration-300 shadow-lg hover:scale-105 active:scale-95"
								title={callStatus === 'connected' ? 'إنهاء المكالمة' : callStatus === 'incoming' ? 'رفض المكالمة' : 'إغلاق'}
							>
								<MdCallEnd className="w-9 h-9 md:w-11 md:h-11" />
							</CallButton>

							{/* Mute button for active calls */}
							{(callStatus === 'connected' || callStatus === 'calling') && (
								<CallButton
									onClick={toggleMute}
									className={`w-20 h-20 md:w-25 md:h-25 ${isMuted ? 'bg-red-500/90' : 'bg-white/20'
										} text-white rounded-md hover:opacity-80 transition-all duration-300 shadow-lg hover:scale-105 active:scale-95`}
									title={isMuted ? 'إلغاء الكتم' : 'كتم الصوت'}
								>
									{isMuted ? (
										<FaMicrophoneSlash className="w-9 h-9 md:w-11 md:h-11" />
									) : (
										<FaMicrophone className="w-9 h-9 md:w-11 md:h-11" />
									)}
								</CallButton>
							)}

							{/* Accept button for incoming calls */}
							{callStatus === 'incoming' && (
								<CallButton
									onClick={acceptCall}
									className="w-20 h-20 md:w-25 md:h-25 bg-green-600/90 text-white rounded-md hover:bg-green-700 transition-all duration-300 shadow-lg hover:scale-105 active:scale-95"
									title="قبول المكالمة"
								>
									<MdCallReceived className="w-9 h-9 md:w-11 md:h-11" />
								</CallButton>
							)}

							{/* Rejoin button for disconnected calls */}
							{callStatus === 'disconnected' && (
								<CallButton
									onClick={rejoinCall}
									className="w-20 h-20 md:w-25 md:h-25 bg-green-600/90 text-white rounded-md hover:bg-green-700 transition-all duration-300 shadow-lg hover:scale-105 active:scale-95"
									title="إعادة الانضمام"
									disabled={!isOnline}
								>
									<GrRefresh className="w-9 h-9 md:w-11 md:h-11" />
								</CallButton>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CallModal;